import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { Check, X, Loader2 } from "lucide-react";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useDebounce } from "@/hooks/useDebounce";
import { TenantFormData } from "@/lib/schemas/tenant";
import { cn } from "@/lib/utils";

interface Step1BasicInfoProps {
  form: UseFormReturn<TenantFormData>;
}

export function Step1BasicInfo({ form }: Step1BasicInfoProps) {
  const [slugStatus, setSlugStatus] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const slug = form.watch("slug");
  const debouncedSlug = useDebounce(slug, 500);

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    form.setValue("name", name);
    const generatedSlug = name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    form.setValue("slug", generatedSlug, { shouldValidate: true });
  };

  // Check slug availability
  useEffect(() => {
    const checkSlug = async () => {
      if (!debouncedSlug || !/^[a-z0-9-]+$/.test(debouncedSlug)) {
        setSlugStatus("idle");
        return;
      }

      setSlugStatus("checking");
      try {
        const { data, error } = await supabase.functions.invoke("Calie_checkSlug", {
          body: { slug: debouncedSlug },
        });

        if (error) throw error;
        setSlugStatus(data.available ? "available" : "taken");
      } catch (err) {
        console.error("Error checking slug:", err);
        setSlugStatus("idle");
      }
    };

    checkSlug();
  }, [debouncedSlug]);

  const SlugIndicator = () => {
    switch (slugStatus) {
      case "checking":
        return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
      case "available":
        return <Check className="h-4 w-4 text-green-500" />;
      case "taken":
        return <X className="h-4 w-4 text-destructive" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Basic Information</h2>
        <p className="text-sm text-muted-foreground">
          Enter the customer's business details
        </p>
      </div>

      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Business Name *</FormLabel>
            <FormControl>
              <Input
                placeholder="Acme Corporation"
                {...field}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="slug"
        render={({ field }) => (
          <FormItem>
            <FormLabel>URL Slug *</FormLabel>
            <FormControl>
              <div className="relative">
                <Input
                  placeholder="acme-corporation"
                  {...field}
                  className={cn(
                    "pr-10",
                    slugStatus === "taken" && "border-destructive focus-visible:ring-destructive"
                  )}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <SlugIndicator />
                </div>
              </div>
            </FormControl>
            <FormDescription>
              This will be used in URLs. Only lowercase letters, numbers, and hyphens.
              {slugStatus === "taken" && (
                <span className="text-destructive block mt-1">
                  This slug is already taken
                </span>
              )}
              {slugStatus === "available" && (
                <span className="text-green-500 block mt-1">
                  This slug is available
                </span>
              )}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="domain"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Custom Domain</FormLabel>
            <FormControl>
              <Input placeholder="acme.com" {...field} />
            </FormControl>
            <FormDescription>
              Optional. The customer's website domain.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
